import React, { FC, useCallback, useEffect, useState } from 'react'

import {
  MAX_ACTIVE_GROUP_MEMBERS_NUMBER,
  MIN_ACTIVE_GROUP_MEMBERS_NUMBER,
  pricePresenter,
} from '@smarty-nx/code-utils'
import { getPlansSlugs, planCollectionNames } from '@smarty-nx/contentful'
import { Button, Level, Typography } from '@smarty-nx/ds'
import cn from 'classnames'

import { content } from './compare-group-plans-calculator.content'
import classes from './compare-group-plans-calculator.module.scss'
import { usePlans } from '../../../hooks'
import GroupLandingPageCTA, {
  GroupLandingCTAType,
} from '../group-landing-page-cta'
import SelectGroupPlanDropdown, {
  GroupPlanOption,
  PlanType,
} from '../select-group-plan-dropdown'

type SelectedPlanData = {
  index: number
  type: PlanType
  originalPrice: number
  finalPrice: number
}

const FAMILY_PLANS = getPlansSlugs(planCollectionNames.FAMILY_PLANS)

export const CompareGroupPlansCalculator: FC = () => {
  const membersCounterValues: number[] = Array.from(
    { length: MAX_ACTIVE_GROUP_MEMBERS_NUMBER },
    (_, i) => i + 2
  )

  const { list: plans } = usePlans(FAMILY_PLANS)
  const familyPlan = plans.find(
    (p) => p.priceData.id === 'SM_EVERYONE_UNLIMITED'
  )

  const groupOwnerPrice = familyPlan?.priceData.finalPrice.value || 0
  const groupMemberPrice = familyPlan?.priceData?.additionalSims?.value || 0

  const [membersCounter, setMembersCounter] = useState<number>(
    MIN_ACTIVE_GROUP_MEMBERS_NUMBER
  )
  const [everyoneUnlimitedTotal, setEveryoneUnlimitedTotal] = useState<number>(
    groupOwnerPrice + groupMemberPrice
  )

  const [selectedOwnerPlan, setSelectedOwnerPlan] =
    useState<SelectedPlanData | null>(null)
  const [selectedMemberPlans, setSelectedMemberPlans] = useState<
    SelectedPlanData[]
  >([])
  const [mixAndMatchOriginalTotal, setMixAndMatchOriginalTotal] =
    useState<number>(0)
  const [mixAndMatchFinalTotal, setMixAndMatchFinalTotal] = useState<number>(0)

  const handleMembersCounter = (count: number) => {
    setMembersCounter(count)
    const additionalMembers = count - 1
    const additionalMembersPrice = additionalMembers * groupMemberPrice
    const total = additionalMembersPrice + groupOwnerPrice
    setEveryoneUnlimitedTotal(total)

    const visibleMemberPlans = selectedMemberPlans.filter(
      (plan) => plan.index <= count - 2
    )
    setSelectedMemberPlans(visibleMemberPlans)
  }

  const handlePlanSelect = (
    value: GroupPlanOption,
    type: PlanType,
    index: number
  ) => {
    if (type === PlanType.Owner) {
      const ownerPlan: SelectedPlanData = {
        index,
        type,
        originalPrice: value.originalPrice,
        finalPrice: value.finalPrice,
      }
      setSelectedOwnerPlan(ownerPlan)
    } else {
      const newMemberPlans = [...selectedMemberPlans]
      const planIndex = newMemberPlans.findIndex(
        (plan) => plan.index === index && plan.type === type
      )

      if (planIndex === -1) {
        newMemberPlans.push({
          index,
          type,
          originalPrice: value.originalPrice,
          finalPrice: value.finalPrice,
        })
      } else {
        newMemberPlans[planIndex].originalPrice = value.originalPrice
        newMemberPlans[planIndex].finalPrice = value.finalPrice
      }
      setSelectedMemberPlans(newMemberPlans)
    }
  }

  const calculateMixAndMatchTotal = useCallback(
    (
      counter: number,
      ownerPlan: SelectedPlanData | null,
      memberPlans: SelectedPlanData[]
    ) => {
      let originalTotal = 0
      let finalTotal = 0

      const visibleMemberPlans = memberPlans.filter(
        (plan) => plan.index <= counter - 2
      )
      visibleMemberPlans.forEach((plan) => {
        originalTotal = originalTotal + plan.originalPrice
        finalTotal = finalTotal + plan.finalPrice
      })
      if (ownerPlan) {
        originalTotal = originalTotal + ownerPlan.originalPrice
        if (visibleMemberPlans.length > 0) {
          finalTotal = finalTotal + ownerPlan.finalPrice
        } else {
          finalTotal = finalTotal + ownerPlan.originalPrice
        }
      }

      setMixAndMatchOriginalTotal(originalTotal)
      setMixAndMatchFinalTotal(finalTotal)
    },
    []
  )

  useEffect(() => {
    const initialTotal = groupOwnerPrice + groupMemberPrice
    setEveryoneUnlimitedTotal(initialTotal)
  }, [groupOwnerPrice, groupMemberPrice, setEveryoneUnlimitedTotal])

  useEffect(() => {
    calculateMixAndMatchTotal(
      membersCounter,
      selectedOwnerPlan,
      selectedMemberPlans
    )
  }, [
    membersCounter,
    selectedOwnerPlan,
    selectedMemberPlans,
    calculateMixAndMatchTotal,
  ])

  return (
    <div id="compare" className={classes.groupPlanCalculator}>
      <Typography
        component="p"
        variant="Kollektif/ExtraLarge"
        data-testid="group-plan-calculator-title-desktop"
        className={classes.desktop}
      >
        {content.title}
      </Typography>
      <div className={classes.mobile} style={{ textAlign: 'left' }}>
        <Typography
          component="p"
          variant="Kollektif/Large"
          data-testid="group-plan-calculator-title-mobile"
        >
          {content.titleMobile}
        </Typography>
      </div>
      <div
        data-testid="group-plan-calculator-description"
        className={classes.description}
      >
        {content.description}
      </div>
      <div className={classes.counterWrapper} data-testid="members-counter">
        {membersCounterValues.map((count, index) => (
          <Button
            type="button"
            level={Level.Tertiary}
            size="tiny"
            className={cn(classes.counterButton, {
              [classes.selected]: membersCounter === count,
            })}
            key={index}
            onClick={() => handleMembersCounter(count)}
            role="region"
            aria-roledescription="carousel"
            data-testid={`members-counter-button-${count.toString()}`}
          >
            {count.toString()}
          </Button>
        ))}
      </div>
      <div
        className={classes.calculatorWrapper}
        data-testid="group-plan-calculator"
      >
        <div className={classes.calculatorColumnWrapper}>
          <div className={classes.calculatorColumn}>
            <div className={classes.columnContent}>
              <div
                className={cn(classes.topSection, classes.everyoneUnlimited)}
              >
                <div className={classes.topSectionItem}>
                  <Typography
                    component="h4"
                    variant="Kollektif/Large"
                    data-testid="everyone-unlimited-title"
                  >
                    {content.everyoneUnlimitedTitle}
                  </Typography>
                  <Typography
                    component="span"
                    variant="Kollektif/ExtraSmallEmphasis"
                    data-testid="everyone-unlimited-subtitle"
                  >
                    {content.everyoneUnlimitedSubtitle}
                  </Typography>
                </div>
                <div className={classes.topSectionItem}>
                  <Typography
                    component="p"
                    variant="Kollektif/ExtraSmall"
                    data-testid="everyone-unlimited-description"
                  >
                    {content.everyoneUnlimitedDescription(
                      groupOwnerPrice,
                      groupMemberPrice
                    )}
                  </Typography>
                </div>
              </div>
              <div className={classes.bottomSection}>
                <div className={classes.bottomSectionItem}>
                  <Typography
                    component="span"
                    variant="Kollektif/ExtraSmallEmphasis"
                    data-testid="group-owner-label"
                  >
                    {content.groupOwnerLabel}
                  </Typography>
                  <Typography
                    component="span"
                    variant="Kollektif/ExtraSmallEmphasis"
                    data-testid="group-owner-cost"
                  >
                    {pricePresenter(groupOwnerPrice)}
                  </Typography>
                </div>
                <div className={classes.desktop}>
                  {Array.from(
                    { length: membersCounter - 1 },
                    (_, k) => k + 1
                  ).map((_, index) => (
                    <div
                      className={cn(
                        classes.bottomSectionItem,
                        classes.bottomSectionItemLeft
                      )}
                      key={index}
                    >
                      <Typography
                        component="span"
                        variant="Kollektif/ExtraSmall"
                        data-testid="group-member-label-desktop"
                      >
                        {content.groupMemberLabel}
                      </Typography>
                      <Typography
                        component="span"
                        variant="Kollektif/ExtraSmallEmphasis"
                        data-testid="group-member-cost-desktop"
                      >
                        {pricePresenter(groupMemberPrice)}
                      </Typography>
                    </div>
                  ))}
                </div>
                <div className={classes.mobile}>
                  <div className={classes.bottomSectionItem}>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmall"
                      data-testid="group-member-label-mobile"
                    >
                      {`+ ${membersCounter - 1} ${
                        membersCounter - 1 === 1 ? 'member' : 'members'
                      }`}
                    </Typography>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmallEmphasis"
                      data-testid="group-member-cost-mobile"
                    >
                      {pricePresenter((membersCounter - 1) * groupMemberPrice)}
                    </Typography>
                  </div>
                </div>
                <div className={classes.line} />
                <div className={classes.bottomSectionItem}>
                  <Typography
                    component="p"
                    variant="Kollektif/ExtraSmall"
                    data-testid="everyone-unlimited-total-label"
                  >
                    {content.everyoneUnlimitedTotalLabel}
                  </Typography>
                  <Typography
                    component="span"
                    variant="Kollektif/ExtraSmallEmphasis"
                    data-testid="everyone-unlimited-total-value"
                  >
                    {pricePresenter(everyoneUnlimitedTotal)}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
          <div className={classes.ctaWrapper}>
            <GroupLandingPageCTA
              buttonType={GroupLandingCTAType.EVERYONE_UNLIMITED}
              dataTestIdPrefix="calculator"
            />
          </div>
        </div>
        <div className={classes.calculatorColumnWrapper}>
          <div className={classes.calculatorColumn}>
            <div className={classes.columnContent}>
              <div className={cn(classes.topSection, classes.mixAndMatch)}>
                <div className={classes.topSectionItem}>
                  <Typography
                    component="h4"
                    variant="Kollektif/Large"
                    data-testid="mix-and-match-title"
                  >
                    {content.mixAndMatchTitle}
                  </Typography>
                  <Typography
                    component="span"
                    variant="Kollektif/ExtraSmallEmphasis"
                    data-testid="mix-and-match-subtitle"
                  >
                    {content.mixAndMatchSubtitle}
                  </Typography>
                </div>
                <div className={classes.topSectionItem}>
                  <Typography
                    component="p"
                    variant="Kollektif/ExtraSmall"
                    data-testid="mix-and-match-description"
                  >
                    {content.mixAndMatchDescription}
                  </Typography>
                </div>
              </div>
              <div className={classes.bottomSection}>
                <div className={classes.bottomSectionItem}>
                  <Typography
                    component="span"
                    variant="Kollektif/ExtraSmallEmphasis"
                    data-testid="group-owner-label"
                  >
                    {content.groupOwnerLabel}
                  </Typography>
                  <SelectGroupPlanDropdown
                    onSelect={handlePlanSelect}
                    type={PlanType.Owner}
                  />
                </div>
                {Array.from(
                  { length: membersCounter - 1 },
                  (_, k) => k + 1
                ).map((_, index) => (
                  <div className={classes.bottomSectionItem} key={index}>
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmall"
                      data-testid="group-member-label"
                    >
                      {content.groupMemberLabel}
                    </Typography>
                    <SelectGroupPlanDropdown
                      onSelect={handlePlanSelect}
                      type={PlanType.Member}
                      index={index}
                    />
                  </div>
                ))}
                <div className={classes.line} />
                <div className={classes.bottomSectionItem}>
                  <Typography
                    component="p"
                    variant="Kollektif/ExtraSmall"
                    data-testid="mix-and-match-total-label"
                  >
                    {content.mixAndMatchTotalLabel}
                  </Typography>
                  <div>
                    <span style={{ textDecoration: 'line-through' }}>
                      <Typography
                        component="span"
                        variant="Kollektif/ExtraSmall"
                        data-testid="mix-and-match-original-total-value"
                      >
                        {selectedMemberPlans.length > 0 &&
                          mixAndMatchOriginalTotal > 0 &&
                          mixAndMatchOriginalTotal !== mixAndMatchFinalTotal &&
                          pricePresenter(mixAndMatchOriginalTotal)}
                      </Typography>
                    </span>
                    &nbsp;
                    <Typography
                      component="span"
                      variant="Kollektif/ExtraSmallEmphasis"
                      data-testid="mix-and-match-final-total-value"
                    >
                      {pricePresenter(mixAndMatchFinalTotal)}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={classes.ctaWrapper}>
            <GroupLandingPageCTA
              buttonType={GroupLandingCTAType.MIX_AND_MATCH}
              dataTestIdPrefix="calculator"
            />
          </div>
        </div>
      </div>
      <div>
        <Typography
          component="span"
          variant="Kollektif/TinyEmphasis"
          data-testid="group-plan-calculator-bottom-note-label"
        >
          {content.bottomNoteLabel}
        </Typography>
        &nbsp;
        <Typography
          component="span"
          variant="Kollektif/Tiny"
          data-testid="group-plan-calculator-title-bottom-note-text"
        >
          {content.bottomNoteText}
        </Typography>
      </div>
    </div>
  )
}

export default CompareGroupPlansCalculator
